import React, { useEffect, useState, useRef } from 'react';

// Reusable Input Component
const InputField = ({ label, id, type = 'text', placeholder, options }) => (
  <div className="w-full">
    <label htmlFor={id} className="block mb-2 font-medium text-slate-900 dark:text-white">
      {label}
    </label>
    {type === 'select' ? (
      <select
        id={id}
        name={id}
        className="block w-full rounded-xl border-0 bg-white dark:bg-slate-700/50 py-3 px-4 text-slate-900 dark:text-white shadow-sm ring-1 ring-inset ring-slate-300 dark:ring-slate-600 placeholder-slate-400 dark:placeholder-slate-300 focus:ring-2 focus:ring-offset-2 focus:ring-offset-background-light dark:focus:ring-offset-background-dark focus:ring-primary transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-700/60"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        id={id}
        name={id}
        placeholder={placeholder}
        className="block w-full rounded-xl border-0 bg-white dark:bg-slate-700/50 py-3 px-4 text-slate-900 dark:text-white shadow-sm ring-1 ring-inset ring-slate-300 dark:ring-slate-600 placeholder-slate-400 dark:placeholder-slate-300 focus:ring-2 focus:ring-offset-2 focus:ring-offset-background-light dark:focus:ring-offset-background-dark focus:ring-primary transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-700/60"
      />
    )}
  </div>
);

// Contact Info Card
const InfoCard = ({ icon, children, style }) => (
  <div
    className="flex gap-4 p-5 bg-gradient-to-r from-white via-slate-50 to-white dark:from-slate-800 dark:via-slate-700/80 dark:to-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-2xl transition-all duration-300 transform group"
    style={style}
  >
    <span className="text-primary text-2xl mt-1 group-hover:scale-110 transition-transform duration-300">{icon}</span>
    <div className="text-sm sm:text-base">{children}</div>
  </div>
);

// Social Icon Link
const SocialIcon = ({ href, ariaLabel, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    className="p-3 rounded-full bg-primary/10 dark:bg-primary/20 hover:bg-gradient-to-tr hover:from-primary/30 hover:to-primary/50 dark:hover:from-primary/40 dark:hover:to-primary/60 text-primary dark:text-white/80 transition-all duration-300 transform hover:scale-110"
  >
    {children}
  </a>
);

const ContactUs = () => {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [infoVisible, setInfoVisible] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sectionRef = useRef(null);

  useEffect(() => {
    // Smooth page fade-in
    setTimeout(() => setFadeIn(true), 50);

    // Page load animations
    const timers = [
      setTimeout(() => setHeaderVisible(true), 100),
      setTimeout(() => setFormVisible(true), 300),
    ];

    // Intersection Observer for contact info section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setInfoVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      timers.forEach(clearTimeout);
      observer.disconnect();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.target);
      const data = {
        name: formData.get('name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        classInterested: formData.get('class'),
        message: formData.get('message'),
      };

      const response = await fetch('/api/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        e.target.reset();
        setTimeout(() => setIsSubmitted(false), 3000);
      } else {
        throw new Error(result.error || 'Failed to submit message');
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`font-display text-slate-800 dark:text-slate-200 relative overflow-hidden transition-opacity duration-1000 ${
        fadeIn ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-200 via-pink-200 to-purple-200 dark:from-purple-900 dark:via-pink-900 dark:to-blue-900 animate-gradient-x opacity-20 z-0"></div>

      {/* Floating Background Elements */}
      <div className="absolute top-20 left-10 w-24 h-24 bg-primary/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute top-40 right-20 w-20 h-20 bg-gold/10 rounded-full blur-2xl animate-float" style={{ animationDelay: '2s' }}></div>
      <div className="absolute bottom-40 left-20 w-28 h-28 bg-primary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
      <div className="absolute bottom-20 right-10 w-16 h-16 bg-gold/10 rounded-full blur-2xl animate-float" style={{ animationDelay: '1s' }}></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        {/* Header Section */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ease-out transform ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/20 px-4 py-2 rounded-full mb-6">
            <span className="text-primary text-lg">📞</span>
            <span className="text-primary font-semibold text-sm">Contact Us</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-6">
            Get in Touch
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We're here to help! Reach out with any questions or inquiries. Our team is dedicated to providing prompt assistance.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12">
          {/* Contact Form */}
          <div
            className={`bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 transition-all duration-1000 ease-out transform ${
              formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <form className="space-y-6" onSubmit={handleSubmit}>
              <InputField label="Your Name" id="name" placeholder="Enter your name" />
              <InputField label="Your Email" id="email" type="email" placeholder="Enter your email" />
              <InputField label="Phone Number" id="phone" type="tel" placeholder="Enter your phone number" />
              <InputField
                label="Class Interested"
                id="class"
                type="select"
                options={[
                  { value: '', label: 'Select a Grade' },
                  { value: '7', label: 'Grade 7' },
                  { value: '8', label: 'Grade 8' },
                  { value: '9', label: 'Grade 9' },
                  { value: '10', label: 'Grade 10' },
                  { value: '11', label: 'Grade 11' },
                  { value: '12', label: 'Grade 12' },
                ]}
              />
              <div>
                <label htmlFor="message" className="block mb-2 font-medium text-slate-900 dark:text-white">
                  Any Queries/Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  placeholder="Enter your queries or message"
                  className="block w-full rounded-xl border-0 bg-white dark:bg-slate-700/50 py-3 px-4 text-slate-900 dark:text-white shadow-sm ring-1 ring-inset ring-slate-300 dark:ring-slate-600 placeholder-slate-400 dark:placeholder-slate-300 focus:ring-2 focus:ring-offset-2 focus:ring-offset-background-light dark:focus:ring-offset-background-dark focus:ring-primary transition-all duration-300 resize-none hover:bg-slate-50 dark:hover:bg-slate-700/60"
                ></textarea>
              </div>

              <div>
                {!isSubmitted ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full justify-center rounded-2xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      'Submit Message'
                    )}
                  </button>
                ) : (
                  <div className="text-center py-4" aria-live="polite">
                    <h3 className="text-lg font-bold text-green-600 dark:text-green-400 mb-2 animate-fadeInUp">
                      Message Sent!
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                      Thank you for contacting us. We'll get back to you soon!
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Contact Info */}
          <div
            className={`space-y-6 transition-all duration-1000 ease-out transform ${
              infoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
            ref={sectionRef}
          >
            {[
              { icon: '📍', content: <><strong>Anestar Victory Junior & Senior School</strong><br />Lanet Ol-Kalou Rd, 273 NAKURU, KENYA</> },
              { icon: '📞', content: '+254 758887739, +254 712915099, +254 712977788' },
              { icon: '✉️', content: 'info@anestarjss.com' },
              { icon: '🕒', content: <><strong>Office Hours:</strong><br />Monday - Friday, 8 AM - 5 PM<br />Saturday, 8 AM - 1 PM<br />Sunday, Closed</> },
            ].map((info, idx) => (
              <InfoCard key={idx} icon={info.icon}>{info.content}</InfoCard>
            ))}

            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-xl border border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Connect With Us</h3>
              <div className="flex gap-4">
                <SocialIcon href="https://www.facebook.com/anestargroup/" ariaLabel="Facebook">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" /></svg>
                </SocialIcon>
                <SocialIcon href="https://www.instagram.com/anestar_group_of_schools/" ariaLabel="Instagram">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 012.153 1.252A4.902 4.902 0 0121.47 6.08c.247.636.416 1.363.465 2.427.048 1.024.06 1.378.06 3.808s-.012 2.784-.06 3.808c-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.252 2.153 4.902 4.902 0 01-2.153 1.252c-.636.247-1.363.416-2.427.465-1.024.048-1.378.06-3.808.06s-2.784-.012-3.808-.06c-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-2.153-1.252A4.902 4.902 0 013.12 17.92c-.247-.636-.416-1.363-.465-2.427-.048-1.024-.06-1.378-.06-3.808s.012-2.784.06-3.808c.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.252-2.153A4.902 4.902 0 016.08 3.12c.636-.247 1.363-.416 2.427-.465C9.53 2.013 9.884 2 12.315 2zM12 7a5 5 0 100 10 5 5 0 000-10zm0 8a3 3 0 110-6 3 3 0 010 6zm6.406-11.845a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5z" /></svg>
                </SocialIcon>
                <SocialIcon href="https://www.youtube.com/@Anestar-schools/videos" ariaLabel="YouTube">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19.812 5.418c.861.23 1.54 1.025 1.762 1.886C21.992 8.71 22 12.014 22 12.014s-.008 3.304-.226 4.71c-.222.861-.901 1.656-1.762 1.886-1.488.426-7.774.426-7.774.426s-6.286 0-7.774-.426a2.585 2.585 0 01-1.762-1.886C2.008 15.318 2 12.014 2 12.014s.008-3.304.226-4.71a2.585 2.585 0 011.762-1.886C5.474 5 11.76 5 11.76 5s6.286 0 7.774.426l.278-.008zM9.75 15.02V9.008l4.822 3.006-4.822 3.006z" /></svg>
                </SocialIcon>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient Animation CSS */}
      <style jsx>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 15s ease infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease forwards;
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ContactUs;
